import React from 'react';
import type { Exercise, ProgramExercise, TrainingProgram, RotationalConfig } from '../../services/types';
import { getImageUrl } from '../../services/api';
import { sanitizedHtmlProps } from '../../utils/sanitizeHtml';
import ExerciseMuscleMap from '../ExerciseMuscleMap';

interface PlannerDay {
  dayNumber: number;
  name: string;
  weekday: number | null;
  exercises: PlannerExercise[];
  rotationConfig: RotationalConfig | null;
}

interface PlannerExercise extends ProgramExercise {
  clientKey: string;
  isNew?: boolean;
}

interface ProgramPrintViewProps {
  program: TrainingProgram;
  days: PlannerDay[];
}

const WEEKDAY_LABELS: Record<number, string> = {
  1: 'Понеделник',
  2: 'Вторник',
  3: 'Сряда',
  4: 'Четвъртък',
  5: 'Петък',
  6: 'Събота',
  7: 'Неделя',
};

const DAY_COLORS = ['#1565C0', '#2E7D32', '#E65100', '#6A1B9A', '#C62828', '#00838F', '#4E342E'];

const MUSCLE_LABELS: Record<string, string> = {
  chest: 'Гръдни',
  back: 'Гръб',
  quadriceps: 'Квадрицепс',
  hamstrings: 'Задно бедро',
  adductors: 'Аддуктори',
  shoulders: 'Рамене',
  arms: 'Ръце',
  core: 'Корем',
  glutes: 'Глутеуси',
  calves: 'Икри',
};

const GOAL_LABELS: Record<string, string> = {
  strength: 'Сила',
  hypertrophy: 'Хипертрофия',
  endurance: 'Издръжливост',
  weight_loss: 'Отслабване',
  general_fitness: 'Обща кондиция',
};

const DIFFICULTY_LABELS: Record<string, string> = {
  beginner: 'Начинаещ',
  intermediate: 'Среднонапреднал',
  advanced: 'Напреднал',
};

const CATEGORY_LABELS: Record<string, string> = {
  strength: 'Силово',
  cardio: 'Кардио',
  flexibility: 'Гъвкавост',
  balance: 'Баланс',
  plyometrics: 'Плиометрика',
  olympic: 'Олимпийско',
  warmup: 'Загряване',
};

const printStyles = `
  .program-print-view { display: none !important; }

  @media print {
    .no-print,
    .ppv-screen-content { display: none !important; }
    .program-print-view {
      display: block !important;
      font-family: 'Segoe UI', Roboto, Arial, sans-serif;
      color: #222;
      font-size: 11px;
      line-height: 1.4;
    }
    .program-print-view * {
      print-color-adjust: exact !important;
      -webkit-print-color-adjust: exact !important;
    }

    .ppv-header {
      text-align: center;
      padding: 12px 0 8px;
      border-bottom: 2px solid #333;
      margin-bottom: 12px;
    }
    .ppv-header h1 {
      font-size: 20px;
      margin: 0 0 6px;
      font-weight: 700;
    }
    .ppv-header-meta {
      display: flex;
      justify-content: center;
      gap: 16px;
      font-size: 11px;
      color: #555;
    }
    .ppv-header-meta span { white-space: nowrap; }

    .ppv-day {
      page-break-inside: avoid;
      margin-bottom: 16px;
    }
    .ppv-day-header {
      color: #fff;
      padding: 6px 12px;
      font-size: 13px;
      font-weight: 700;
      border-radius: 4px 4px 0 0;
    }
    .ppv-day-body {
      display: flex;
      gap: 12px;
      border: 1px solid #ddd;
      border-top: none;
      padding: 8px;
      border-radius: 0 0 4px 4px;
    }
    .ppv-day-table-wrap {
      flex: 1;
      min-width: 0;
    }
    .ppv-day-map-wrap {
      flex: 0 0 200px;
      display: flex;
      align-items: flex-start;
      justify-content: center;
    }

    .ppv-table {
      width: 100%;
      border-collapse: collapse;
      font-size: 11px;
    }
    .ppv-table th, .ppv-table td {
      border: 1px solid #ccc;
      padding: 3px 6px;
      text-align: left;
    }
    .ppv-table th {
      background: #f5f5f5;
      font-weight: 600;
      font-size: 10px;
      text-transform: uppercase;
    }
    .ppv-table td.ppv-num {
      text-align: center;
      width: 28px;
    }
    .ppv-table .ppv-superset-a { background: rgba(25, 118, 210, 0.06); }
    .ppv-table .ppv-superset-b { background: rgba(46, 125, 50, 0.06); }
    .ppv-table .ppv-superset-c { background: rgba(230, 81, 0, 0.06); }

    .ppv-details-section {
      page-break-before: always;
    }
    .ppv-details-title {
      font-size: 16px;
      font-weight: 700;
      text-align: center;
      margin: 12px 0;
      padding-bottom: 6px;
      border-bottom: 2px solid #333;
    }
    .ppv-exercise-card {
      page-break-inside: avoid;
      border: 1px solid #ddd;
      border-radius: 4px;
      margin-bottom: 10px;
      padding: 8px 10px;
    }
    .ppv-exercise-card h3 {
      font-size: 13px;
      margin: 0 0 4px;
    }
    .ppv-exercise-meta {
      display: flex;
      gap: 12px;
      font-size: 10px;
      color: #555;
      margin-bottom: 4px;
      flex-wrap: wrap;
    }
    .ppv-exercise-muscles {
      font-size: 10px;
      margin-bottom: 4px;
    }
    .ppv-exercise-muscles strong { color: #333; }
    .ppv-intro {
      page-break-inside: avoid;
      border: 1px solid #ddd;
      border-radius: 4px;
      padding: 10px 14px;
      margin-bottom: 14px;
      background: #fafafa;
    }
    .ppv-intro h2 {
      font-size: 14px;
      margin: 0 0 6px;
      font-weight: 700;
    }
    .ppv-intro-body {
      font-size: 11px;
      line-height: 1.5;
    }
    .ppv-intro-body h3 { font-size: 12px; margin: 8px 0 2px; }
    .ppv-intro-body p { margin: 4px 0; }
    .ppv-intro-body ul, .ppv-intro-body ol { padding-left: 18px; margin: 4px 0; }

    .ppv-description {
      font-size: 10px;
      margin-top: 4px;
    }
    .ppv-description h3 { font-size: 11px; margin: 4px 0 2px; }
    .ppv-description p { margin: 2px 0; }
    .ppv-description ul, .ppv-description ol { padding-left: 16px; margin: 2px 0; }
    .ppv-video {
      font-size: 10px;
      margin-top: 4px;
    }
    .ppv-video a { color: #1565C0; text-decoration: none; word-break: break-all; }
    .ppv-mistakes {
      margin-top: 4px;
      font-size: 10px;
    }
    .ppv-mistakes strong { color: #c62828; }
    .ppv-mistakes ul { margin: 2px 0; padding-left: 16px; }
    .ppv-mistakes li { margin-bottom: 2px; }
    .ppv-instructions {
      font-size: 10px;
      margin-top: 4px;
    }
    .ppv-instructions h3 { font-size: 11px; margin: 4px 0 2px; }
    .ppv-instructions p { margin: 2px 0; }
    .ppv-instructions ul, .ppv-instructions ol { padding-left: 16px; margin: 2px 0; }
    .ppv-caution {
      background: #fff8e1;
      border-left: 3px solid #f9a825;
      padding: 4px 8px;
      font-size: 10px;
      margin-top: 4px;
    }
    .ppv-anatomy-images {
      display: flex;
      gap: 8px;
      margin-top: 6px;
      flex-wrap: wrap;
    }
    .ppv-anatomy-images img {
      max-width: 150px;
      max-height: 150px;
      border: 1px solid #ddd;
      border-radius: 4px;
    }
  }
`;

function buildMuscleValues(exercises: PlannerExercise[]): Record<string, number> {
  const values: Record<string, number> = {};
  for (const pe of exercises) {
    const ex = pe.exercise;
    const primary = ex.primaryMuscleGroup || ex.muscleGroup;
    if (primary && primary !== 'cardio') {
      values[primary] = (values[primary] || 0) + 1.0;
    }
    if (ex.secondaryMuscleGroup && ex.secondaryMuscleGroup !== 'cardio') {
      values[ex.secondaryMuscleGroup] = (values[ex.secondaryMuscleGroup] || 0) + 0.5;
    }
    if (ex.tertiaryMuscleGroup && ex.tertiaryMuscleGroup !== 'cardio') {
      values[ex.tertiaryMuscleGroup] = (values[ex.tertiaryMuscleGroup] || 0) + 0.25;
    }
  }
  return values;
}

function getUniqueExercises(days: PlannerDay[]): Exercise[] {
  const seen = new Set<number>();
  const result: Exercise[] = [];
  for (const day of days) {
    for (const pe of day.exercises) {
      if (!seen.has(pe.exercise.id)) {
        seen.add(pe.exercise.id);
        result.push(pe.exercise);
      }
    }
  }
  return result;
}

const ROTATION_COLORS = ['#1d4ed8', '#c2410c', '#15803d', '#7c3aed', '#b45309', '#0f766e'];

const SUPERSET_CLASSES = ['ppv-superset-a', 'ppv-superset-b', 'ppv-superset-c'];

function getSupersetClass(group: string | null | undefined, dayExercises: PlannerExercise[]): string {
  if (!group) return '';
  const groups = Array.from(new Set(dayExercises.filter(e => e.supersetGroup).map(e => e.supersetGroup!)));
  const idx = groups.indexOf(group);
  return idx >= 0 ? SUPERSET_CLASSES[idx % SUPERSET_CLASSES.length] : '';
}

function formatMuscles(ex: Exercise): string {
  const parts: string[] = [];
  const primary = ex.primaryMuscleGroup || ex.muscleGroup;
  if (primary) parts.push(MUSCLE_LABELS[primary] || primary);
  if (ex.secondaryMuscleGroup) parts.push(MUSCLE_LABELS[ex.secondaryMuscleGroup] || ex.secondaryMuscleGroup);
  if (ex.tertiaryMuscleGroup) parts.push(MUSCLE_LABELS[ex.tertiaryMuscleGroup] || ex.tertiaryMuscleGroup);
  return parts.join(' / ');
}

const ProgramPrintView: React.FC<ProgramPrintViewProps> = ({ program, days }) => {
  const uniqueExercises = getUniqueExercises(days);

  return (
    <>
      <style>{printStyles}</style>
      <div className="program-print-view">
        {/* Header */}
        <div className="ppv-header">
          <h1>{program.name}</h1>
          <div className="ppv-header-meta">
            <span>Цел: {GOAL_LABELS[program.goal] || program.goal}</span>
            <span>Трудност: {DIFFICULTY_LABELS[program.difficulty] || program.difficulty}</span>
            <span>Продължителност: {program.durationWeeks} седм.</span>
            <span>Тренировки/седм.: {program.workoutsPerWeek}</span>
            <span>Дни: {days.length}</span>
          </div>
          {program.description && (
            <div style={{ fontSize: '10px', color: '#666', marginTop: '4px' }}>{program.description}</div>
          )}
        </div>

        {/* Introductional / marketing information */}
        {program.introduction && program.introduction.trim() !== '' && (
          <div className="ppv-intro">
            <h2>Въведение</h2>
            <div className="ppv-intro-body" {...sanitizedHtmlProps(program.introduction)} />
          </div>
        )}

        {/* Per-day blocks */}
        {days.map((day, dayIdx) => {
          const color = DAY_COLORS[dayIdx % DAY_COLORS.length];
          const weekdayLabel = day.weekday ? WEEKDAY_LABELS[day.weekday] : null;

          const renderDayBlock = (
            key: string,
            headerLabel: string,
            headerColor: string,
            exercises: PlannerExercise[],
          ) => {
            const muscleValues = buildMuscleValues(exercises);
            const hasMuscles = Object.keys(muscleValues).length > 0;

            return (
              <div key={key} className="ppv-day">
                <div className="ppv-day-header" style={{ backgroundColor: headerColor }}>
                  {headerLabel}
                </div>
                <div className="ppv-day-body">
                  <div className="ppv-day-table-wrap">
                    {exercises.length === 0 ? (
                      <div style={{ padding: '8px', color: '#999', fontStyle: 'italic' }}>Няма упражнения</div>
                    ) : (
                      <table className="ppv-table">
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Упражнение</th>
                            <th>Серии</th>
                            <th>Повт.</th>
                            <th>Тежест (kg)</th>
                            <th>Бележки</th>
                          </tr>
                        </thead>
                        <tbody>
                          {exercises.map((pe, idx) => {
                            const ssClass = getSupersetClass(pe.supersetGroup, exercises);
                            return (
                              <tr key={pe.clientKey} className={ssClass}>
                                <td className="ppv-num">{idx + 1}</td>
                                <td>
                                  {pe.exercise.name}
                                  {pe.supersetGroup && (
                                    <span style={{ fontSize: '9px', color: '#888', marginLeft: '4px' }}>
                                      [{pe.supersetGroup}]
                                    </span>
                                  )}
                                </td>
                                <td className="ppv-num">{pe.targetSets ?? '—'}</td>
                                <td className="ppv-num">{pe.targetReps ?? '—'}</td>
                                <td className="ppv-num">{pe.targetWeight ?? '—'}</td>
                                <td style={{ fontSize: '9px', maxWidth: '120px' }}>{pe.notes || ''}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    )}
                  </div>
                  {hasMuscles && (
                    <div className="ppv-day-map-wrap">
                      <ExerciseMuscleMap muscleValues={muscleValues} hideAnnotations />
                    </div>
                  )}
                </div>
              </div>
            );
          };

          const dayPrefix = `Ден ${day.dayNumber}: ${day.name}${weekdayLabel ? ` (${weekdayLabel})` : ''}`;

          if (day.rotationConfig && day.rotationConfig.rotationLabels.length > 1) {
            return day.rotationConfig.rotationLabels.map((label, rotIdx) => {
              const rotExercises = day.exercises.filter(
                (ex) => ex.rotationIndexes == null || ex.rotationIndexes.includes(rotIdx)
              );
              const rotColor = ROTATION_COLORS[rotIdx % ROTATION_COLORS.length];
              return renderDayBlock(
                `${day.dayNumber}-rot-${rotIdx}`,
                `${dayPrefix} — ${label}`,
                rotColor,
                rotExercises,
              );
            });
          }

          return renderDayBlock(
            String(day.dayNumber),
            dayPrefix,
            color,
            day.exercises,
          );
        })}

        {/* Exercise details section */}
        {uniqueExercises.length > 0 && (
          <div className="ppv-details-section">
            <div className="ppv-details-title">Описание на упражненията</div>
            {uniqueExercises.map(ex => (
              <div key={ex.id} className="ppv-exercise-card">
                <h3>{ex.name}</h3>
                <div className="ppv-exercise-meta">
                  {ex.category && <span>Категория: {CATEGORY_LABELS[ex.category] || ex.category}</span>}
                  {ex.equipment && <span>Оборудване: {ex.equipment}</span>}
                  {ex.difficulty && <span>Трудност: {DIFFICULTY_LABELS[ex.difficulty] || ex.difficulty}</span>}
                </div>
                <div className="ppv-exercise-muscles">
                  <strong>Мускули:</strong> {formatMuscles(ex)}
                </div>
                {ex.description && (
                  <div className="ppv-description">
                    <strong>Описание:</strong>
                    <div {...sanitizedHtmlProps(ex.description)} />
                  </div>
                )}
                {ex.instructions && (
                  <div className="ppv-instructions">
                    <strong>Инструкции:</strong>
                    <div {...sanitizedHtmlProps(ex.instructions)} />
                  </div>
                )}
                {ex.youtubeVideoUrl && (
                  <div className="ppv-video">
                    <strong>Видео:</strong>{' '}
                    <a href={ex.youtubeVideoUrl} target="_blank" rel="noopener noreferrer">
                      {ex.youtubeVideoUrl}
                    </a>
                  </div>
                )}
                {ex.commonMistakes && ex.commonMistakes.length > 0 && (
                  <div className="ppv-mistakes">
                    <strong>Чести грешки:</strong>
                    <ul>
                      {ex.commonMistakes.map((m, i) => (
                        <li key={i}>{typeof m === 'string' ? m : (m as { description: string }).description}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {ex.cautionNotes && (
                  <div className="ppv-caution">
                    ⚠ <strong>Внимание:</strong>{' '}
                    <span {...sanitizedHtmlProps(ex.cautionNotes)} />
                  </div>
                )}
                {ex.anatomyImages && ex.anatomyImages.length > 0 && (
                  <div className="ppv-anatomy-images">
                    {ex.anatomyImages.map((img, i) => (
                      <img key={i} src={getImageUrl(img)} alt={`${ex.name} анатомия ${i + 1}`} />
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default ProgramPrintView;
